import React, { useState, useEffect } from "react";
import {
  Box,
  Modal,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Stack,
  Divider,
} from "@mui/material";

import TaskAltIcon from "@mui/icons-material/TaskAlt";
import API from "../../utils/api";

export default function CreateNewModal({
  open,
  onClose,
  onCreated,
}) {
  // ------- TASK FIELDS -------
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assignedToId, setAssignedToId] = useState("");
  const [priority, setPriority] = useState("Medium");

  // ------- DROPDOWN DATA (TASK ASSIGNEES) -------
  const [users, setUsers] = useState([]);

  // LOAD USERS WHEN MODAL OPENS
  useEffect(() => {
    if (!open) return;

    const fetchUsers = async () => {
      try {
        const res = await API.get("/users/");
        setUsers(res.data);
      } catch (err) {
        console.error("Failed to load users:", err);
      }
    };

    fetchUsers();
  }, [open]);

  const reset = () => {
    setTitle("");
    setDescription("");
    setDueDate("");
    setAssignedToId("");
    setPriority("Medium");
  };

  const closeModal = () => {
    reset();
    onClose();
  };

  // BACKEND CALL
  const handleCreate = async () => {
    if (!title.trim()) return;
    try {
      await API.post("/tasks/", {
        title: title.trim(),
        description,
        assigned_to: assignedToId || null,
        priority,
        due_date: dueDate || null,
      });

      if (onCreated) onCreated();
      closeModal();
    } catch (err) {
      console.error("Create New error:", err);
    }
  };

  // MODAL STYLES
  const modalStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",

  width: {
    xs: "92vw",   // ✅ mobile: fit screen
    sm: 480,      // tablets
    md: 520,      // desktop (original size)
  },

  maxHeight: "90dvh",      // ✅ prevents vertical overflow
  overflowY: "auto",       // ✅ scroll inside modal

  bgcolor: "background.paper",
  borderRadius: { xs: 2, sm: 4 },
  boxShadow: "0px 8px 24px rgba(0,0,0,0.15)",
};


  return (
    <Modal open={open} onClose={closeModal}>
      <Box sx={modalStyles}>
        {/* HEADER */}
        <Box sx={{ bgcolor: "#1a73e8", color: "white", p: 3 }}>
          <Typography variant="h5" fontWeight={700}>
            Create New Task
          </Typography>
          <Typography variant="body2" opacity={0.85}>
            Add task details and assign it to a team member.
          </Typography>
        </Box>

        {/* CONTENT */}
        <Box sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <TaskAltIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={700}>
              Task Details
            </Typography>
          </Stack>

          <TextField
            fullWidth
            required
            label="Title"
            sx={{ mb: 3 }}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Description"
            sx={{ mb: 3 }}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>Assigned To</InputLabel>
              <Select
                label="Assigned To"
                value={assignedToId}
                onChange={(e) => setAssignedToId(e.target.value)}
              >
                <MenuItem value="">Unassigned</MenuItem>
                {users.length === 0 ? (
                  <MenuItem disabled>Loading users...</MenuItem>
                ) : (
                  users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.username ||
                        `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
                        u.email}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                label="Priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                <MenuItem value="Low">Low</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="High">High</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="date"
              label="Due Date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </Box>

        <Divider />

        {/* FOOTER */}
        <Box sx={{ p: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="outlined" onClick={closeModal}>
            Cancel
          </Button>

          <Button
            variant="contained"
            sx={{ textTransform: "none", fontWeight: 700 }}
            onClick={handleCreate}
            disabled={!title.trim()}
          >
            Create Task
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
